<template>
  <NuxtLoadingIndicator color="#F53F3F" :height="3" />
  <NuxtLayout>
    <NuxtPage :page-key="resolvePageKey" />
    <!-- Dynamic component to avoid warnings if the copilot layer is not installed. -->
    <component :is="copilotUiComponent" v-if="copilotUiComponent" />
  </NuxtLayout>
  <BaseToastHost />
</template>

<script setup>
// 在 Nuxt 3/4 中使用 NuxtPage 内置的 transition，
// 页面过渡样式已在下方 CSS 中定义，无需额外的 GSAP 配置
import { computed, watch, onMounted, onUnmounted } from 'vue'
import { clearAllBodyScrollLocks } from '@/utils/modal'
import { matchesWebviewReturnTarget, WEBVIEW_RETURN_SCENE_LIST } from '@/config/webviewReturn'

const runtimeConfig = useRuntimeConfig()
const route = useRoute()
const webviewReturnStates = WEBVIEW_RETURN_SCENE_LIST.map((scene) => ({
  scene,
  state: useState(scene.stateKey, () => null),
}))

/**
 * @description 生成 NuxtPage 实例 key。订单列表切 tab 只改 query.status，必须用 path，避免整页重建清空搜索关键字。
 * @param {import('vue-router').RouteLocationNormalizedLoaded} route - 当前路由。
 * @returns {string}
 */
const resolvePageKey = (route) => {
  const path = String(route?.path || '').replace(/\/+$/, '') || '/'
  if (path === '/store/account/orders' || path.endsWith('/store/account/orders')) {
    return path
  }
  return route.fullPath
}

/**
 * @description Extract a normalized route path from a full path.
 * @param {string} fullPath - Route full path including optional query or hash.
 * @returns {string} Normalized route path.
 */
const getRoutePath = (fullPath) =>
  String(fullPath || '')
    .split(/[?#]/)[0]
    .replace(/\/+$/, '') || '/'

/**
 * @description Decide whether global route cleanup should preserve a configured return scroll.
 * @reason Configured pages restore their own snapshots after returning from detail.
 * @param {string} toFullPath - Destination route full path.
 * @param {string} fromFullPath - Source route full path.
 * @returns {boolean} Whether the global scroll-to-top should be skipped.
 */
const shouldPreserveWebviewReturnScroll = (toFullPath, fromFullPath) => {
  const fromPath = getRoutePath(fromFullPath)
  const toPath = getRoutePath(toFullPath)

  return webviewReturnStates.some(({ scene, state }) => {
    return (
      scene.returnPath === toPath &&
      matchesWebviewReturnTarget(scene, fromPath) &&
      state.value?.routeFullPath === toFullPath
    )
  })
}

// 监听路由变化，确保跳转页面时清除所有滚动锁定
watch(
  () => route.fullPath,
  (toFullPath, fromFullPath) => {
    clearAllBodyScrollLocks()
    if (import.meta.client && !shouldPreserveWebviewReturnScroll(toFullPath, fromFullPath)) {
      window.scrollTo({ top: 0, left: 0 })
    }
  }
)
const copilotEnabled = computed(() => runtimeConfig.public?.copilotEnabled === true)
const copilotUiAvailable = computed(() => runtimeConfig.public?.copilotUiAvailable === true)
const copilotUiComponent = computed(() =>
  copilotEnabled.value && copilotUiAvailable.value ? 'AICopilotButton' : null
)

// 禁止页面级双指捏合缩放（iOS 等会忽略 viewport user-scalable=no，需用 touch 阻止默认）
function preventPagePinchZoom(e) {
  if (e.touches.length >= 2 && !e.target.closest('.arco-image-preview')) {
    e.preventDefault()
  }
}
onMounted(() => {
  if (import.meta.client) {
    document.addEventListener('touchmove', preventPagePinchZoom, { passive: false })
  }
})
onUnmounted(() => {
  if (import.meta.client) {
    document.removeEventListener('touchmove', preventPagePinchZoom)
  }
})
</script>

<style lang="scss">
/* 使用 CSS 配合 GSAP 动画类名，或者直接在 definePageMeta 中使用 hooks */
.page-enter-active,
.page-leave-active {
  transition:
    opacity 0.5s ease,
    transform 0.5s ease;
}

.page-enter-from {
  opacity: 0;
  transform: translateY(20px);
}

.page-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}
</style>
